import React, {useCallback, useEffect, useRef, useState} from 'react';

import {Popup, PopupPlacement, Skeleton} from '@gravity-ui/uikit';

import {block} from '../../../utils/cn';
import {ClipboardButtonWrapper} from '../../ClipboardButtonWrapper';
import {UserAvatar} from '../../UserAvatar';

import {clearTimeouts} from './clearTimeouts';
import {getUserName} from './getUserName';
import {toAvatarUser} from './toAvatarUser';
import type {OrganizationUserCardProps, Timeout} from './types';

import './OrganizationUserCard.scss';

const b = block('organization-user-card');

const POPUP_PLACEMENT = ['bottom-start', 'top-start'] as PopupPlacement;
const DEFAULT_SHOW_DELAY = 500;
const DEFAULT_HIDE_DELAY = 200;

export const OrganizationUserCard = React.memo<OrganizationUserCardProps>(
    ({
        className,
        anchorRef,
        user,
        initiallyOpen,
        showDelay = DEFAULT_SHOW_DELAY,
        hideDelay = DEFAULT_HIDE_DELAY,
        onShow,
        onHide,
    }) => {
        const [popupOpen, setPopupOpen] = useState(
            initiallyOpen === undefined ? !anchorRef : false,
        );
        const [userData, setUserData] = useState(typeof user === 'function' ? undefined : user);

        const showTimeout = useRef<Timeout>();
        const hideTimeout = useRef<Timeout>();

        const showPopup = useCallback(() => {
            clearTimeouts([showTimeout, hideTimeout]);

            showTimeout.current = setTimeout(() => {
                setPopupOpen(true);

                if (onShow) {
                    onShow();
                }
            }, showDelay);

            if (!userData && typeof user === 'function') {
                user().then((data) => setUserData(data));
            }
        }, [showTimeout, hideTimeout, showDelay, setPopupOpen, user, userData, setUserData]);

        const hidePopup = useCallback(() => {
            clearTimeouts([showTimeout, hideTimeout]);

            hideTimeout.current = setTimeout(() => {
                setPopupOpen(false);

                if (onHide) {
                    onHide();
                }
            }, hideDelay);
        }, [showTimeout, hideTimeout, hideDelay, setPopupOpen]);

        useEffect(() => {
            if (initiallyOpen) {
                showPopup();
            }

            return () => {
                clearTimeouts([showTimeout]);
            };
        }, []);

        useEffect(() => {
            const anchorElement = anchorRef && anchorRef.current;

            if (!anchorElement) {
                return;
            }

            anchorElement.addEventListener('mouseenter', showPopup);
            anchorElement.addEventListener('mouseleave', hidePopup);

            return () => {
                if (anchorElement) {
                    anchorElement.removeEventListener('mouseenter', showPopup);
                    anchorElement.removeEventListener('mouseleave', hidePopup);
                }
            };
        }, [anchorRef]);

        const onPopupMouseEnter = useCallback(() => {
            clearTimeouts([hideTimeout]);
        }, [hideTimeout]);

        let cardContent;

        if (userData) {
            cardContent = (
                <div className={b('content')}>
                    <UserAvatar
                        className={b('avatar')}
                        user={toAvatarUser(userData)}
                        federation={Boolean(userData.federation)}
                        size="m"
                    />
                    <div className={b('info')}>
                        <div className={b('name')}>{getUserName(userData)}</div>
                        {userData.email && (
                            <div className={b('email')}>
                                <ClipboardButtonWrapper text={userData.email}>
                                    {userData.email}
                                </ClipboardButtonWrapper>
                            </div>
                        )}
                        {userData.phoneNumber && (
                            <div className={b('phone-number')}>
                                <ClipboardButtonWrapper text={userData.phoneNumber}>
                                    {userData.phoneNumber}
                                </ClipboardButtonWrapper>
                            </div>
                        )}
                    </div>
                </div>
            );
        } else {
            cardContent = (
                <div className={b('content', {loading: true})}>
                    <Skeleton className={b('avatar-skeleton')} />
                    <div className={b('info')}>
                        <Skeleton className={b('name-skeleton')} />
                        <Skeleton className={b('email-skeleton')} />
                    </div>
                </div>
            );
        }

        if (!anchorRef) {
            return <div className={b(null, className)}>{cardContent}</div>;
        }

        return (
            <Popup
                anchorRef={anchorRef}
                className={b(null, className)}
                open={popupOpen}
                keepMounted={false}
                placement={POPUP_PLACEMENT}
                onMouseEnter={onPopupMouseEnter}
                onMouseLeave={hidePopup}
            >
                {cardContent}
            </Popup>
        );
    },
);

OrganizationUserCard.displayName = 'OrganizationUserCard';

export * from './types';
